import React, { useMemo, useState, useContext, useEffect, useRef } from 'react';

import classes from './Checklist.module.css';
import { SORT_CATEGORIES, SHARE_CATEGORIES } from '../../config';

import { doc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../../config';
import { generateId, getItemCategories, restoreScrollPosition } from '../../config/helpers';

import SaveWork from '../Forms/SaveWork';

import Button from '../UI/Button';
import Loading from '../UI/Loading';
import Share from '../Share/Share';

import ChecklistSection from './ChecklistSection';
import SuggestionChips from './SuggestionChips';

import GroupMembers from '../Group/GroupMembers';
import SelectOptions from '../UI/SelectOptions';
import GroupNotes from '../Modals/GroupNotes';
import EditGroup from '../Group/EditGroup';

import ChecklistAdd from './ChecklistAdd';

import { UserContext } from '../../store/user-context';
import SearchAndCategories from './SearchAndCategories';
import ItemDetail from './ItemDetail';
import InlineAdd from './InlineAdd';

const OWN_NEED_TOOLTIP_KEY = 'tooltip_own_need_seen';

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M18 6L6 18M6 6L18 18" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const InfoIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="var(--brand-primary, #2be494)" strokeWidth="1.6"/>
    <circle cx="12" cy="8" r="1" fill="var(--brand-primary, #2be494)"/>
    <path d="M12 11v5" stroke="var(--brand-primary, #2be494)" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);

const OwnNeedTooltip = ({ onDismiss }) => {
  const [dismissing, setDismissing] = useState(false);

  const handleDismiss = () => {
    localStorage.setItem(OWN_NEED_TOOLTIP_KEY, 'true');
    setDismissing(true);
    // Let the fade-out play before actually removing it from the tree
    setTimeout(onDismiss, 200);
  };

  return (
    <div className={`${classes.ownNeedTooltip} ${dismissing ? classes.ownNeedTooltipHidden : ''}`}>
      <span className={classes.ownNeedTooltipArrow} aria-hidden="true" />
      <span className={classes.ownNeedTooltipIcon} aria-hidden="true"><InfoIcon /></span>
      <div className={classes.ownNeedTooltipBody}>
        <p className={classes.ownNeedTooltipHeading}>How this works</p>
        <p className={classes.ownNeedTooltipText}>Own = you're bringing it.</p>
        <p className={classes.ownNeedTooltipText}>Need = you still need to get it.</p>
      </div>
      <button
        className={classes.ownNeedTooltipClose}
        onClick={handleDismiss}
        aria-label="Dismiss"
      >
        <CloseIcon />
      </button>
    </div>
  );
};

const SectionItems = ({ sectionItems, group, festivalInfo, filterMembers, activeCategory, searchQuery, onDetail, onShowToast, ownNeedTooltip, optionalItems }) => {
     const isAllView = activeCategory === '';
     //drag and drop ordering
     const [working, setWorking] = useState(false);
     const [currentDrag, setCurrentDrag] = useState();
     const [currentDragOver, setCurrentDragOver] = useState();

     //when items are drag and dropped the group items must be reordered to suit
    //handle sort is called from child component ChecklistItem
    const  handleDragEnd = async (e) => {
        setWorking(false);
        
        //don't do anything if they haven't dragged or dropped or it's still working
        if (working || !currentDrag || !currentDragOver) return;
        //do nothing if they've dropped the item onto itself
        if (currentDrag.id === currentDragOver.id) return;
        e?.preventDefault();
        setWorking(true);
        
        //duplicate items
        const _items = [...group.items];
        const dragIndex = _items.findIndex( item => item.id === currentDrag.id);
        const dragOverIndex = _items.findIndex( item => item.id === currentDragOver.id);
        //get drag item object
        const dragItemContent = _items[dragIndex];
        
        //make sure drag item category array includes the category they are dropping into (in case they are dragging and dropping items between categories)
        const dragItemCategories = getItemCategories(dragItemContent);
        if (!dragItemCategories.includes(currentDragOver.category)) {
            //find category they were dragging from and replace with drop category
            const currentCatIndex = dragItemCategories.indexOf(currentDrag.category);
            if (currentCatIndex !== -1) dragItemCategories[currentCatIndex] = currentDragOver.category;
            dragItemContent.categories = dragItemCategories;
        }
        //remove previous drag item from the group items
        _items.splice(dragIndex,1);
        //add to where the drop item is 
        _items.splice(dragOverIndex, 0, dragItemContent)
        //reset order property according to new position in array
        _items.map( (item, index) => item.order = index);
        //console.log(_items.map(item => (item.order+' '+item.name+' '+item.categories[0]))); return
        {/*update order of items in groups collection in firebase*/}
        {if (group && group.id) {
            try {
                const groupRef = doc(db, 'groups', group.id);
                await updateDoc(groupRef, { 
                items: _items
                });
            } catch (err) {
                alert("There was a problem updating your group.")
            }
        }}
        setCurrentDrag();
        setCurrentDragOver();
        setWorking(false);
    }

    //handleDragStart function is called from child component ChecklistItem.
    const handleDragStart = async (e, id, name, category) => {
        setCurrentDrag({id: id, name: name, category:category})
    }
    
    //handleDragOver function is called from child component ChecklistItem. Add prevent default to on drag over functionality to call the sort function faster
    // Bails out when the hovered card hasn't actually changed — dragover fires
    // continuously (native drag every frame, touch on every touchmove), and
    // setting a brand-new object every time re-renders the whole list for the
    // entire drag gesture instead of only when there's something new to reflect.
    const handleDragOver = async (e, id, name, category) => {
        e?.preventDefault();
        setCurrentDragOver(prev => (prev?.id === id ? prev : {id: id, name: name, category:category}))
    }

    const sectionMatchesSearch = (section) =>
      section.items?.some(item =>
        !searchQuery || item.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );

    const visibleSections = (sectionItems || []).filter(section =>
      isAllView ? sectionMatchesSearch(section) : section.title === activeCategory
    );

    const hasVisibleItems = isAllView
      ? visibleSections.length > 0
      : visibleSections.some(sectionMatchesSearch);

    // The Own/Need explainer sits right after the very first gear item shown
    // (whichever section that turns out to be), not above the whole list.
    const firstNonEmptySectionIndex = visibleSections.findIndex(section => section.items?.length > 0);

    return (
        <>
        {
            visibleSections.map( (section, index ) => (
                <React.Fragment key={index}>
                  {isAllView && section.items?.length > 0 && (
                    <p className={classes.sectionHeading}>{section.title}</p>
                  )}
                  <ChecklistSection
                    group={group}
                    festivalInfo={festivalInfo}
                    section={section}
                    filterMembers={filterMembers}
                    searchQuery={searchQuery}
                    handleDragStart={handleDragStart}
                    handleDragOver={handleDragOver}
                    handleDragEnd={handleDragEnd}
                    onDetail={onDetail}
                    onShowToast={onShowToast}
                    insertAfterFirstItem={index === firstNonEmptySectionIndex ? ownNeedTooltip : null}
                  />
                  {/* On the All tab a category with no items shows no heading (see
                      above) — suppress its suggestions there too, so a chip row
                      never appears without something labelling which category it's
                      for. A single-category tab has no such ambiguity. */}
                  {!searchQuery && (!isAllView || section.items?.length > 0) && (
                    <SuggestionChips group={group} category={section.title} optionalItems={optionalItems} />
                  )}
                </React.Fragment>
            ))
        }
        {!hasVisibleItems && (
          <p className={classes.emptyText}>
            {searchQuery
              ? `No results for '${searchQuery}'`
              : activeCategory
                ? `No ${activeCategory} items yet. Tap + to add some`
                : 'No items yet. Tap + to add some'}
          </p>
        )}
       </>
    )
}

const Checklist = ( { group, festivalInfo, activeCategory, setActiveCategory, onAddItem, onShowToast, onAddCategory, onRenameCategory, onDeleteCategory, onSetCategoryIcon, onReorderCategories } ) => {

    //get previous sort mode from user context
    const UserCtx = useContext(UserContext);
   
    const [sortMode, setSortMode] = useState( UserCtx?.sortMode? UserCtx.sortMode : 'categories');
    const [searchQuery, setSearchQuery] = useState('');
    const preCategoryRef = useRef(activeCategory);

    // One-time Own/Need explainer — authenticated users only, never shown again once dismissed
    const [showOwnNeedTooltip, setShowOwnNeedTooltip] = useState(() => {
      const isGuest = !auth.currentUser || auth.currentUser.isAnonymous;
      if (isGuest) return false;
      return localStorage.getItem(OWN_NEED_TOOLTIP_KEY) !== 'true';
    });

    // Root.js swaps Home/My Gear/Need/Pack in and out of the same shared
    // `.content` scroll container rather than mounting a fresh scrollable
    // wrapper per screen, so without this My Gear opens at whatever scroll
    // position the previous screen was left at. Reapplied on the next frame
    // too — on iOS Safari a tap can leave momentum/rubber-band scrolling
    // still settling on `.content`, which can silently override a scrollTop
    // reset that only happens once, synchronously, on mount.
    useEffect(() => {
      const contentEl = document.querySelector('.content');
      if (contentEl) contentEl.scrollTop = 0;
      const raf = requestAnimationFrame(() => {
        if (contentEl) contentEl.scrollTop = 0;
      });
      return () => cancelAnimationFrame(raf);
    }, []);

    // Keep preCategoryRef updated with the last explicitly-selected non-All category
    useEffect(() => {
      if (activeCategory !== '') preCategoryRef.current = activeCategory;
    }, [activeCategory]);

    const handleSearchChange = (query) => {
      if (query && activeCategory !== '') {
        preCategoryRef.current = activeCategory;
        setActiveCategory('');
      } else if (!query) {
        setActiveCategory(preCategoryRef.current || '');
      }
      setSearchQuery(query);
    };
    const [selectedItem, setSelectedItem] = useState(null);
    // ItemDetail intentionally scrolls `.content` to the top on open (see
    // ItemDetail.js) — since it's the same shared scroll container as this
    // list, that leaves the list itself sitting at scrollTop 0 underneath.
    // Captured here right before opening detail, then reapplied on the way
    // back so returning lands on the same item/category instead of the top.
    const savedScrollRef = useRef(0);
    const openItemDetail = (item) => {
      const contentEl = document.querySelector('.content');
      if (contentEl) savedScrollRef.current = contentEl.scrollTop;
      setSelectedItem(item);
    };
    const closeItemDetail = () => {
      setSelectedItem(null);
      restoreScrollPosition(savedScrollRef.current);
    };
    const [inlineAdding, setInlineAdding] = useState(false);
    //const initialMembers = [...group?.memberIds, 'unassigned'];
    const [filterMembers, setFilterMembers] = useState([...group?.memberIds, 'unassigned']);
    //console.log('filterMembers',filterMembers)
    const [categories, setCategories] = useState(group?.categories);
    const [shareMode, setShareMode] = useState('all');
    const [groupEditMode, setGroupEditMode] = useState(false);

    // Optional (default: false) catalog items, used to show "+ Item"
    // suggestion chips under each category. Fetched once here rather than
    // per-section/per-render — SuggestionChips just filters this by category.
    const [optionalItems, setOptionalItems] = useState([]);
    useEffect(() => {
      const fetchOptionalItems = async () => {
        try {
          const q = query(collection(db, 'defaultitems'), where('default', '==', false));
          const querySnapshot = await getDocs(q);
          const items = [];
          querySnapshot.forEach(d => items.push(d.data()));
          setOptionalItems(items);
        } catch (err) {
          console.log('Error fetching optional items:', err);
        }
      };
      fetchOptionalItems();
    }, []);

    //console.log(group.id)
;    const sectionItems = useMemo(() => {
        //list and organise items according to sort mode 
        const organisedItems = [];
        switch (sortMode) {
            case 'categories':
            //organise by categories
            categories.forEach( (category, index) => {
                //get any group items that have this category
                const categoryItems = group?.items.filter( item => ( getItemCategories(item).includes(category) ))
                //Sort alphabetically (name)
                if (categoryItems.length>1) categoryItems.sort((a, b) => a.name.localeCompare(b.name));
                //set up object for the category and add items as an array
                const categoryObj = { id: index, title: category, items: categoryItems }
                //add category object to filtered items array
                organisedItems.push(categoryObj);
            });  
            //sortedItems.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            break;
            case 'members':
            //organise by members
            group?.members.forEach( (member, index) => {
                //get any group items that have been allocated to this member
                //apply filters - get number of items according to which member they are filtering
                const memberItems = group?.items.filter( ( item ) => {
                    const result = item.organise.find (org => org.assigned === member.id)
                    if ( result )  return ( item )
                });
                //Sort alphabetically (name)
                if (memberItems.length>1) memberItems.sort((a, b) => a.name.localeCompare(b.name));
                //set up object for the category and add items as an array
                const memberObj = { id: member.id, title: member.name, items: memberItems }
                //add member object to filtered items array
                organisedItems.push(memberObj);
            });  
            break;
            default:
            //default is to sort newest to oldest
            //sortedEntries.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        }
        return organisedItems;
    }, [group, sortMode, categories]);

    useEffect(() => {
        if (!sortMode) return;
        //sort mode has changed - update context
        UserCtx.setSortMode(sortMode)
      }, [sortMode]);

      useEffect(() => {
        if (!group) return;
        //group has changed - reset member filter
        //console.log('group has changed',group);
        setCategories(group.categories);
        setFilterMembers([...group.memberIds, 'unassigned']);
      }, [group]);

    
    const renderChecklistTop = () => {
        return (
            <SearchAndCategories
                categories={group?.categories || []}
                categoryIcons={group?.categoryIcons || {}}
                activeCategory={activeCategory}
                setActiveCategory={setActiveCategory}
                searchQuery={searchQuery}
                setSearchQuery={handleSearchChange}
                onAddItem={(cat) => { setActiveCategory(cat || activeCategory); setInlineAdding(true); }}
                onAddCategory={() => onAddCategory && onAddCategory()}
                onRenameCategory={(oldName, newName) => onRenameCategory && onRenameCategory(oldName, newName)}
                onDeleteCategory={(cat) => onDeleteCategory && onDeleteCategory(cat)}
                onSetCategoryIcon={(cat, key) => onSetCategoryIcon && onSetCategoryIcon(cat, key)}
                onReorderCategories={(cats) => onReorderCategories && onReorderCategories(cats)}
            />
        );
    }

    const renderChecklistHeading = () => {
        return (
        <div className={classes.checklistHeading}>
            <div className={classes.checklistHeadingTextHolder}>
                <div className={classes.checklistHeadingText}>List</div>
                <div className={classes.checklistHeadingUnderline}>&nbsp;</div>
            </div>

           

        </div>
        )
    }



    if (!sectionItems) return (
    <Loading msg='Loading...'/>
    )
    
    if (groupEditMode && group) return (
        <EditGroup group={group} onConfirm={() => setGroupEditMode(!groupEditMode)}/>
    )

    if (selectedItem) return (
        <ItemDetail item={selectedItem} group={group} onBack={closeItemDetail}/>
    );

    return (

        <div className={classes.checklistContainer}>

        {renderChecklistTop()}

        {/*renderChecklistHeading()*/}

        {/*<div className={classes.checklistSaveBar}>
            <div className={classes.checklistSaveGroup}>

                <SaveWork group={group}/>
                
                <SelectOptions 
                title='Share' 
                icon='share' 
                options={SHARE_CATEGORIES} 
                selectedOption={shareMode}
                setSelectedOption={setShareMode}
                />
                
            </div>

           <ChecklistAdd group={group}/>

        </div>
    */}
<div className={classes.checklistSections}>
          {inlineAdding && (
            <InlineAdd
              activeCategory={activeCategory}
              group={group}
              onClose={() => setInlineAdding(false)}
              onShowToast={onShowToast}
              screen="gear"
            />
          )}
          <SectionItems sectionItems={sectionItems} group={group} festivalInfo={festivalInfo} filterMembers={filterMembers} activeCategory={activeCategory} searchQuery={searchQuery} optionalItems={optionalItems} onDetail={openItemDetail}
              onShowToast={onShowToast}
              ownNeedTooltip={showOwnNeedTooltip ? <OwnNeedTooltip onDismiss={() => setShowOwnNeedTooltip(false)} /> : null}/>
        </div>
        
        </div>

    );

}

export default Checklist;